<template>
  <div class="home">
    <Spinner v-if="user.uid && loading" class="spinning" />
    <div v-if="(!user.uid || document.isDeleted == true) && !loading">
      <Login
        title="Hypermatic Enterprise Login"
        :error="error"
        :deleted="document.isDeleted"
        @auth="handleLogin"
        @logout="handleLogout"
        @error="handleError"
      />
    </div>
    <div v-else-if="!loading && user.uid">
      <Header
        @logout="handleLogout"
        :document="document"
        :org="document.org"
        :name="document.name"
        :noLinks="true"
      />

      <div class="customers__wrapper">
        <div class="customers__content">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              Enterprise Overview
            </h3>
            <button @click="createNewQuote" class="customers__button">
              Create New Customer Quote
            </button>
          </div>
          <table class="customers__summary">
            <thead>
              <tr>
                <td>
                  Total Annual Billable Amount
                </td>
                <td>
                  Total Active Enterprise Accounts
                </td>
                <td>
                  Total Inactive Enterprise Accounts
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <b>{{ grandTotalAnnualInvoicesAmount }}</b> USD
                  <br />
                  <a href="#calendar">View Yearly Renewal Calendar</a>
                </td>

                <td>
                  <b>{{ activeCustomers.length }}</b> Customers
                  <br />
                  <a href="#active">View Active Customers</a>
                </td>
                <td>
                  <b>{{ inactiveCustomers.length }}</b> Customers
                  <br />
                  <a href="#inactive">View Inactive Customers</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="customers__content" id="actions">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              Renewals to Action
            </h3>
          </div>
          <table class="customers__summary">
            <thead>
              <tr>
                <td>
                  Gumroad Renewals To Check This Month ({{
                    gumroadsDueThisMonth.length
                  }})
                </td>
                <td>
                  Invoices To Send This Month ({{
                    invoicesDueThisMonth.length
                  }})
                </td>
                <td>
                  Unpaid Quotes/Invoices To Follow Up ({{
                    outstandingInvoicePayments.length
                  }})
                </td>
                <td>
                  Invoices Paid This Month ({{ invoicesPaidThisMonth.length }})
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <ul>
                    <li
                      v-for="(renewal, index) in gumroadsDueThisMonth"
                      :key="index"
                    >
                      <div class="customers__icontext">
                        <img
                          v-if="renewal.doc.billing == 'gumroad'"
                          class="customers__billing"
                          src="@/assets/gumroad.png"
                          title="Payment via Gumroad"
                        />
                        <img
                          v-else
                          class="customers__billing"
                          src="@/assets/invoice.png"
                          title="Payment via Invoice"
                        />

                        <span>
                          <a :href="`#customer_${renewal.doc.slug}`">{{
                            renewal.doc.org
                          }}</a>
                        </span>
                      </div>

                      {{ renewal.subscription.name }}<br />
                      (Gumroad Renew Date:
                      <b>{{
                        formatRenewalDate(renewal.subscription.renewal_date)
                      }}</b
                      >)
                      <button
                        @click="
                          updateSubscriptionInvoiceStatus({
                            renewal: renewal,
                            invoice_sent: true,
                            invoice_paid: true
                          })
                        "
                        class="customers__button"
                        type="button"
                      >
                        Mark Subscription as Paid
                      </button>
                    </li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li
                      v-for="(renewal, index) in invoicesDueThisMonth"
                      :key="index"
                    >
                      <div class="customers__icontext">
                        <img
                          v-if="renewal.doc.billing == 'gumroad'"
                          class="customers__billing"
                          src="@/assets/gumroad.png"
                          title="Payment via Gumroad"
                        />
                        <img
                          v-else
                          class="customers__billing"
                          src="@/assets/invoice.png"
                          title="Payment via Invoice"
                        />

                        <span>
                          <a :href="`#customer_${renewal.doc.slug}`">{{
                            renewal.doc.org
                          }}</a>
                        </span>
                      </div>

                      {{ renewal.subscription.name }}<br />
                      (Renewal Date:
                      <b>{{
                        formatRenewalDate(renewal.subscription.renewal_date)
                      }}</b
                      >)
                      <button
                        @click="
                          updateSubscriptionInvoiceStatus({
                            renewal: renewal,
                            invoice_sent: true,
                            invoice_paid: false
                          })
                        "
                        class="customers__button"
                        type="button"
                      >
                        Mark Invoice as Sent
                      </button>
                    </li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li
                      v-for="(renewal, index) in outstandingInvoicePayments"
                      :key="index"
                    >
                      <img
                        v-if="renewal.doc.billing == 'gumroad'"
                        class="customers__billing"
                        src="@/assets/gumroad.png"
                        title="Payment via Gumroad"
                      />
                      <img
                        v-else
                        class="customers__billing"
                        src="@/assets/invoice.png"
                        title="Payment via Invoice"
                      />

                      <a :href="`#customer_${renewal.doc.slug}`">{{
                        renewal.doc.org
                      }}</a
                      ><br />
                      {{ renewal.subscription.name }} (Email Sent Date:
                      <b>{{
                        formatRenewalDate(renewal.subscription.invoice_sent)
                      }}</b
                      >)

                      <button
                        @click="
                          updateSubscriptionInvoiceStatus({
                            renewal: renewal,
                            invoice_sent: false,
                            invoice_paid: true
                          })
                        "
                        class="customers__button"
                        type="button"
                      >
                        Mark Invoice as Paid
                      </button>
                    </li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li
                      v-for="(renewal, index) in invoicesPaidThisMonth"
                      :key="index"
                    >
                      <img
                        v-if="renewal.doc.billing == 'gumroad'"
                        class="customers__billing"
                        src="@/assets/gumroad.png"
                        title="Payment via Gumroad"
                      />
                      <img
                        v-else
                        class="customers__billing"
                        src="@/assets/invoice.png"
                        title="Payment via Invoice"
                      />

                      <a :href="`#customer_${renewal.doc.slug}`">{{
                        renewal.doc.org
                      }}</a
                      ><br />
                      {{ renewal.subscription.name }} (Invoice Paid Date:
                      <b>{{
                        formatRenewalDate(renewal.subscription.invoice_paid)
                      }}</b
                      >)

                      <button
                        @click="
                          updateSubscriptionInvoiceStatus({
                            renewal: renewal,
                            invoice_sent: false,
                            invoice_paid: false
                          })
                        "
                        class="customers__button"
                        type="button"
                      >
                        Reset Invoice to Unsent/Unpaid
                      </button>
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="customers__content" id="calendar">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              Renewals Calendar
            </h3>
          </div>

          <Calendar
            @clicked="handleCalendarEventClick"
            :events="calendarEvents"
          />
        </div>

        <div class="customers__content" id="active">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              All Active Enterprise Customers ({{ activeCustomers.length }})
            </h3>
          </div>

          <table class="customers__table">
            <thead>
              <tr>
                <td>Company</td>
                <td>Billing</td>
                <td>Subscriptions</td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="doc in activeCustomers"
                :key="doc.id"
                :id="`customer_${doc.slug}`"
              >
                <!-- <td><div class="customers__logo" v-html="doc.logo"></div></td> -->

                <td>
                  <a
                    :href="`https://${doc.domain}/`"
                    target="_blank"
                    :title="doc.org"
                    ><Logos :name="doc.slug"
                  /></a>
                </td>

                <td>
                  <div
                    class="customers__icontext"
                    v-if="doc.billing == 'invoice'"
                  >
                    <img
                      class="customers__billing"
                      src="@/assets/invoice.png"
                      title="Payment via Invoice"
                    />
                    <span>Payment via <b>Invoice / Bank Transfer</b></span>
                  </div>
                  <div class="customers__icontext" v-else>
                    <img
                      class="customers__billing"
                      src="@/assets/gumroad.png"
                      title="Payment via Gumroad"
                    />
                    <span>Payment via <b>Gumroad</b></span>
                  </div>

                  <!-- <p>
                    <span v-for="item in doc.address" :key="item"
                      >{{ item }}<br
                    /></span>
                  </p> -->
                  <p>
                    <span
                      ><a
                        :href="
                          `https://docs.google.com/document/u/3/?q=${doc.org}`
                        "
                        target="_blank"
                        >Search Google Docs ("{{ doc.org }}")</a
                      ></span
                    >
                  </p>

                  <ul>
                    <li v-for="email in doc.contact_emails" :key="email">
                      {{ email }}
                    </li>
                  </ul>

                  <div class="customers__split">
                    <button
                      class="customers__button"
                      type="button"
                      @click="
                        openPdfForm({
                          doc,
                          subscriptions: doc.subscriptions,
                          isQuote: true
                        })
                      "
                    >
                      Open Quote PDF ({{ doc.subscriptions.length }})
                    </button>

                    <button
                      v-if="doc.billing == 'invoice'"
                      class="customers__button"
                      type="button"
                      @click="
                        openPdfForm({
                          doc,
                          subscriptions: doc.subscriptions,
                          isQuote: false
                        })
                      "
                    >
                      Open Invoice PDF ({{ doc.subscriptions.length }})
                    </button>
                  </div>
                </td>

                <td>
                  <ul
                    v-for="subscription in doc.subscriptions"
                    :key="subscription.key"
                    :class="
                      subscription.active
                        ? 'customers__subscriptions'
                        : 'customers__subscriptions customers__subscriptions--inactive'
                    "
                  >
                    <li>
                      <div class="customers__icontext">
                        <PluginIcon
                          class="customers__icon"
                          :pluginName="subscription.name"
                        />

                        <span
                          >{{ subscription.name }} (<a
                            :href="
                              `https://admin.hypermatic.com/firefoo/${subscription.key}`
                            "
                            target="_blank"
                            >{{ subscription.key }}</a
                          >)</span
                        >
                      </div>
                    </li>

                    <li>
                      <b>{{ formatRenewalDate(subscription.renewal_date) }}</b>
                      Renewal Date
                    </li>
                    <li>
                      <b>{{ subscription.seats }}</b> Seats
                    </li>
                    <li>
                      <b>{{
                        formatNumberToCurrency(subscription.invoice_total)
                      }}</b>
                      {{ doc.slug == "vfc" ? "EUR" : "USD" }} (<b>{{
                        pricePerSeat(
                          subscription.seats,
                          subscription.invoice_total
                        )
                      }}</b>
                      {{ doc.slug == "vfc" ? "EUR" : "USD" }} / Seat)
                    </li>
                    <li>
                      <div class="customers__split">
                        <button
                          class="customers__button"
                          type="button"
                          @click="
                            openPdfForm({
                              doc,
                              subscriptions: [subscription],
                              isQuote: true
                            })
                          "
                        >
                          Open {{ subscription.name }} Quote PDF
                        </button>

                        <button
                          v-if="doc.billing == 'invoice'"
                          class="customers__button"
                          type="button"
                          @click="
                            openPdfForm({
                              doc,
                              subscriptions: [subscription],
                              isQuote: false
                            })
                          "
                        >
                          Open {{ subscription.name }} Invoice PDF
                        </button>
                      </div>
                    </li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div id="invoice" class="customers__content">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              PDF Generator
            </h3>

            <button
              @click="downloadPdf"
              class="customers__button"
              type="button"
            >
              Download PDF {{ formData.isQuote ? "Quote" : "Invoice" }}
            </button>
          </div>

          <div class="invoice">
            <form class="invoice__form">
              <fieldset>
                <legend>PDF Type</legend>

                <label>
                  <input v-model="formData.isQuote" type="checkbox" />
                  Render as a Quote
                </label>

                <input
                  type="text"
                  v-model="formData.heading"
                  placeholder="Heading (eg. Renewal Quote or Invoice)"
                  required
                />
              </fieldset>

              <!-- Invoice Details -->
              <fieldset>
                <legend>Invoice Details</legend>

                <input
                  type="text"
                  v-model="formData.invoice.number"
                  :disabled="formData.isQuote"
                  placeholder="Invoice Number"
                  required
                />
                <input v-model="formData.invoice.date" type="date" required />
              </fieldset>

              <!-- Customer Details -->
              <fieldset>
                <legend>Customer Details</legend>
                <input
                  type="text"
                  v-model="formData.customer.name"
                  placeholder="Customer Name"
                  required
                />
                <textarea
                  v-model="formData.customer.bill_to"
                  placeholder="Bill To (one per line)"
                  rows="5"
                  required
                ></textarea>
              </fieldset>

              <!-- Purchase Order -->
              <fieldset>
                <legend>Purchase Order</legend>
                <input
                  type="text"
                  v-model="formData.purchaseOrder"
                  placeholder="Purchase Order Number"
                  :disabled="formData.isQuote"
                />
              </fieldset>

              <!-- Items -->
              <fieldset>
                <legend>Line Items</legend>
                <div
                  v-for="(item, index) in formData.items"
                  :key="index"
                  class="line-item"
                >
                  <input
                    type="text"
                    v-model="item.description"
                    placeholder="Description"
                    required
                  />
                  <input
                    v-model="item.price"
                    type="number"
                    placeholder="Price"
                    required
                  />

                  <button
                    class="customers__button customers__button--destroy"
                    type="button"
                    @click="removeItem(index)"
                  >
                    Remove
                  </button>
                </div>

                <button
                  class="customers__button"
                  type="button"
                  @click="addItem"
                >
                  Add New Line Item
                </button>
              </fieldset>

              <!-- Optional metadata -->
              <fieldset>
                <legend>Optional Footnotes</legend>
                <textarea
                  v-model="formData.metadata"
                  placeholder="Optional metadata to display below grand total (e.g., additional notes or terms)"
                  rows="4"
                ></textarea>
              </fieldset>

              <!-- Company Details -->
              <fieldset>
                <legend>Company Details</legend>
                <input
                  type="text"
                  v-model="formData.company.name"
                  placeholder="Company Name"
                  required
                />
                <input
                  type="text"
                  v-model="formData.company.alias"
                  placeholder="Company Alias (optional)"
                />
                <input
                  type="text"
                  v-model="formData.company.website"
                  placeholder="Website URL (optional)"
                />
                <input
                  type="text"
                  v-model="formData.company.email"
                  placeholder="Email (optional)"
                />
                <textarea
                  v-model="formData.company.address"
                  placeholder="Address"
                  required
                  rows="2"
                ></textarea>
              </fieldset>

              <!-- Payment Details -->
              <fieldset>
                <legend>Payment Details</legend>
                <input
                  type="text"
                  v-model="formData.paymentDetails.bankName"
                  :disabled="formData.isQuote"
                  placeholder="Bank Name"
                  required
                />
                <input
                  type="text"
                  v-model="formData.paymentDetails.bankAddress"
                  :disabled="formData.isQuote"
                  placeholder="Bank Address"
                  required
                />
                <input
                  type="text"
                  v-model="formData.paymentDetails.swiftCode"
                  :disabled="formData.isQuote"
                  placeholder="SWIFT Code"
                  required
                />
                <input
                  type="text"
                  v-model="formData.paymentDetails.accountName"
                  :disabled="formData.isQuote"
                  placeholder="Account Name"
                  required
                />
                <input
                  type="text"
                  v-model="formData.paymentDetails.bsb"
                  :disabled="formData.isQuote"
                  placeholder="BSB"
                  required
                />
                <input
                  type="text"
                  v-model="formData.paymentDetails.accountNumber"
                  :disabled="formData.isQuote"
                  placeholder="Account Number"
                  required
                />
              </fieldset>

              <!-- Toggle Sections -->
              <fieldset>
                <legend>Include/Exclude Sections</legend>
                <label>
                  <input
                    v-model="formData.includeDunsDetails"
                    type="checkbox"
                  />
                  Include DUNS Number
                </label>
                <label>
                  <input
                    v-model="formData.includeCompanyDetails"
                    type="checkbox"
                  />
                  Include Company Details
                </label>
                <label>
                  <input
                    v-model="formData.includeCustomerDetails"
                    type="checkbox"
                  />
                  Include Customer Details
                </label>
                <label>
                  <input
                    v-model="formData.includePaymentDetails"
                    type="checkbox"
                  />
                  Include Payment Details
                </label>
              </fieldset>
            </form>

            <!-- PDF Preview -->
            <div v-if="pdfPreviewUrl" class="invoice__preview">
              <iframe class="invoice__iframe" :src="pdfPreviewUrl"></iframe>
            </div>
          </div>
        </div>

        <div class="customers__content" id="inactive">
          <div class="firefoo__header">
            <h3 class="firefoo__heading">
              All Inactive Enterprise Customers ({{ inactiveCustomers.length }})
            </h3>
          </div>

          <table class="customers__table">
            <thead>
              <tr>
                <td>Company</td>

                <td>Subscriptions</td>
              </tr>
            </thead>
            <tr
              v-for="doc in inactiveCustomers"
              :key="doc.id"
              :id="`customer_${doc.slug}`"
            >
              <td>{{ doc.org }}</td>
              <td>
                <ul
                  v-for="subscription in doc.subscriptions"
                  :key="subscription.key"
                  :class="
                    subscription.active
                      ? 'customers__subscriptions'
                      : 'customers__subscriptions customers__subscriptions--inactive'
                  "
                >
                  <li>
                    <PluginIcon
                      class="customers__icon"
                      :pluginName="subscription.name"
                    />

                    {{ subscription.name }} (<a
                      :href="
                        `https://admin.hypermatic.com/firefoo/${subscription.key}`
                      "
                      target="_blank"
                      >{{ subscription.key }}</a
                    >)
                  </li>

                  <li>
                    <b>{{ subscription.seats }}</b> Seats
                  </li>
                </ul>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import Login from "@/components/Login.vue";
import Spinner from "@/components/Spinner.vue";
import Header from "@/components/Header.vue";
import Logos from "@/components/Logos.vue";
import PluginIcon from "@/components/PluginIcon.vue";
import Calendar from "@/components/Calendar.vue";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import jsPDF from "jspdf";


// const db = firebase.firestore();
// const storageRef = firebase.storage().ref();

export default {
  name: "Home",
  components: {
    Login,
    Spinner,
    Header,
    Logos,
    PluginIcon,
    Calendar
  },

  watch: {
    formData: {
      deep: true,
      handler() {
        this.generatePdfInvoice();
      }
    }
  },

  mounted() {
    // Example Gumroad Key: 2724E727-BB0C462B-88D973D6-CCAEACF8

    // adam@figmatic.com
    // UID: hLswwIyXoaaPBfolWSAKCh9Sbiu2
    // console.log("preauth");
    firebase.auth().onAuthStateChanged(async user => {
      // console.log("auth");
      // document.title = "Loading...";
      if (user) {
        // if (
        //   user.email !== `crypto_${this.$route.params.userId}@figmaticapp.com`
        // ) {
        //   firebase.auth().signOut();
        // } else {

        // Check if user account is adam@figmatic.com
        if (user.uid !== "MGlrxkO5IPdi9SKb2wEivyLGBfU2") {
          this.error = "404 - Page Not Found";
          this.handleLogout();
        } else {
          this.user = user;
          const docs = await this.fetchAllDocuments();
          // console.log(doc);
          this.documents = docs;
        }

        this.loading = false;

        this.generatePdfInvoice(); // Generate the first PDF preview when the component loads
        // }
      } else {
        document.title = "Hypermatic Enterprise Login";
        this.user = {};
        this.loading = false;
      }
    });
  },
  data() {
    return {
      permalink: "",
      newEmail: "",
      loading: true,
      documents: [],
      document: { isDeleted: false },
      user: {},
      error: "",
      formData: this.defaultFormData(true),
      pdfPreviewUrl: null // Stores the Blob URL for the PDF preview
    };
  },
  computed: {
    activeCustomers() {
      return this.documents.filter(doc => doc.disabled === false);
    },
    inactiveCustomers() {
      return this.documents.filter(doc => doc.disabled === true);
    },
    calendarEvents() {
      const now = new Date();
      const years = [
        now.getFullYear() - 1,
        now.getFullYear(),
        now.getFullYear() + 1
      ]; // Previous, current, and next year

      return this.activeCustomers
        .map(doc =>
          doc.subscriptions.flatMap(subscription =>
            years.map(year => {
              const originalDate = new Date(
                subscription.renewal_date.seconds * 1000
              );
              const recurringDate = new Date(originalDate);
              recurringDate.setFullYear(year); // Set the year to the specified year

              return {
                start: recurringDate.toISOString(), // Start date in ISO format
                end: recurringDate.toISOString(), // End date (same as start for single-day events)
                title: `${doc.org} (${subscription.name})`, // Event title
                classes: [doc.billing, doc.slug] // Classes for styling (from doc)
              };
            })
          )
        )
        .reduce((acc, curr) => acc.concat(curr), []);
    },
    grandTotalAnnualInvoicesAmount() {
      let total = 0;

      this.activeCustomers.forEach(doc => {
        doc.subscriptions.forEach(sub => {
          total += parseFloat(sub.invoice_total) || 0; // Parse as float, fallback to 0 if invalid
        });
      });

      return this.formatNumberToCurrency(total.toFixed(2)); // Ensure 2 decimal places
    },
    renewalsDueThisMonth() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // Zero-indexed (0 = January)

      // Flatten subscriptions and filter by current month and year
      return this.activeCustomers
        .map(doc =>
          doc.subscriptions.map(sub => ({
            doc,
            subscription: sub
          }))
        )
        .reduce((acc, curr) => acc.concat(curr), [])
        .filter(({ subscription }) => {
          const renewalDate = new Date(
            subscription.renewal_date.seconds * 1000
          );
          if (subscription.invoice_paid === undefined) return false;
          if (subscription.invoice_sent === undefined) return false;
          const paidDate = new Date(subscription.invoice_paid.seconds * 1000);
          const sentDate = new Date(subscription.invoice_sent.seconds * 1000);

          return (
            subscription.active === true &&
            renewalDate.getMonth() === currentMonth &&
            paidDate.getFullYear() < currentYear &&
            sentDate.getFullYear() < currentYear
          );
        });
    },
    invoicesDueThisMonth() {
      return this.renewalsDueThisMonth.filter(
        renewal => renewal.doc.billing === "invoice"
      );
    },
    gumroadsDueThisMonth() {
      return this.renewalsDueThisMonth.filter(
        renewal => renewal.doc.billing === "gumroad"
      );
    },
    outstandingInvoicePayments() {
      // Flatten subscriptions and filter by current month and year

      const now = new Date();
      const currentYear = now.getFullYear();

      return this.activeCustomers
        .map(doc =>
          doc.subscriptions.map(sub => ({
            doc,
            subscription: sub
          }))
        )
        .reduce((acc, curr) => acc.concat(curr), [])
        .filter(({ subscription }) => {
          if (subscription.invoice_paid === undefined) return false;
          if (subscription.invoice_sent === undefined) return false;
          const sentDate = new Date(subscription.invoice_sent.seconds * 1000);
          const paidDate = new Date(subscription.invoice_paid.seconds * 1000);
          return (
            subscription.active === true &&
            sentDate.getFullYear() === currentYear &&
            paidDate.getFullYear() < currentYear
          );
        });
    },
    invoicesPaidThisMonth() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // Zero-indexed (0 = January)

      // Flatten subscriptions and filter by current month and year
      return this.activeCustomers
        .map(doc =>
          doc.subscriptions.map(sub => ({
            doc,
            subscription: sub
          }))
        )
        .reduce((acc, curr) => acc.concat(curr), [])
        .filter(({ subscription }) => {
          if (subscription.invoice_paid === undefined) return false;
          const renewalDate = new Date(
            subscription.renewal_date.seconds * 1000
          );
          const paidDate = new Date(subscription.invoice_paid.seconds * 1000);
          return (
            subscription.active === true &&
            renewalDate.getFullYear() === currentYear &&
            renewalDate.getMonth() === currentMonth &&
            paidDate.getFullYear() === currentYear &&
            paidDate.getMonth() === currentMonth
          );
        });
    }
  },

  methods: {
    async fetchAllDocuments() {
      const snapshot = await firebase
        .firestore()
        .collection("enterprise_users")
        // .where("disabled", "==", false)
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id, // Include the document ID
        ...doc.data() // Spread the document data
      }));
    },
    async handleLogin(authenticated) {
      // console.log(authenticated.user);

      this.user = authenticated.user;
      // const id = this.$route.params.orgId
      //   ? this.$route.params.orgId
      //   : this.user.uid;
    },
    handleLogout() {
      firebase
        .auth()
        .signOut()
        .then(() => {
          // Sign-out successful.
          this.user = {};
        })
        .catch(() => {
          // console.log(error);
          // An error happened.
        });
    },
    async updateSubscriptionInvoiceStatus(dataObj) {
      const { renewal, invoice_sent, invoice_paid } = dataObj;
      const { subscription, doc } = renewal;
      const { key, permalink } = subscription;

      // TODO - Update invoice_sent and invoice_paid to be dates instead of booleans

      try {
        // Fetch the document snapshot from the "enterprise_users" collection
        const snapshot = await firebase
          .firestore()
          .collection("enterprise_users")
          .doc(doc.id)
          .get();

        // Check if the document exists
        if (!snapshot.exists) {
          // console.error("Document not found");
        }

        // Get the document data
        const data = snapshot.data();
        const subscriptions = data.subscriptions || [];

        // Update the subscriptions array
        const updatedSubscriptions = subscriptions.map(sub => {
          if (sub.permalink === permalink && sub.key === key) {
            // Update the invoice_sent value
            if (invoice_sent === false && invoice_paid === false) {
              const now = new Date();
              const renewalDate = subscription.renewal_date.toDate();
              const oneYearEarlier = new Date(
                now.getFullYear() - 1,
                renewalDate.getMonth(),
                renewalDate.getDate()
              );

              return {
                ...subscription,
                invoice_sent: oneYearEarlier,
                invoice_paid: oneYearEarlier
              };
            } else if (invoice_sent && invoice_paid) {
              return {
                ...subscription,
                invoice_sent: new Date(),
                invoice_paid: new Date()
              };
            } else if (invoice_sent) {
              return {
                ...subscription,
                invoice_sent: new Date()
              };
            } else if (invoice_paid) {
              return {
                ...subscription,
                invoice_paid: new Date()
              };
            }
          }
          return subscription;
        });

        // Update the document with the modified subscriptions array
        await firebase
          .firestore()
          .collection("enterprise_users")
          .doc(doc.id)
          .update({ subscriptions: updatedSubscriptions });

        alert("Subscription updated successfully! Please refresh the page");
      } catch (error) {
        // console.error("Error updating subscription:", error);
      }
    },
    handleCalendarEventClick(event) {
      document.getElementById(`customer_${event.classes[1]}`).scrollIntoView({
        behavior: "smooth"
      });
    },
    createNewQuote() {
      this.resetFormData(true);
      this.formData.heading = "Quote";
      document.getElementById(`invoice`).scrollIntoView();
    },
    openPdfForm(dataObj) {
      const { doc, subscriptions, isQuote } = dataObj;

      this.resetFormData(isQuote);
      this.formData.customer.name = doc.org;

      if (doc.address) {
        doc.address.forEach(line => {
          this.formData.customer.bill_to += `${line}\n`;
        });
      }

      const items = [];

      subscriptions.forEach(sub => {
        const description = `${sub.name} Enterprise License (${sub.seats} Seats)`;
        const price = sub.invoice_total;
        items.push({ description, price });
      });

      this.formData.items = items;

      document.getElementById(`invoice`).scrollIntoView();
    },
    resetFormData(isQuote) {
      const data = this.defaultFormData(isQuote);
      this.formData = data;
    },
    defaultFormData(isQuote) {
      const australianDate = new Intl.DateTimeFormat("en-AU", {
        timeZone: "Australia/Sydney",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(new Date());

      // Convert the formatted date to the desired format (YYYY-MM-DD)
      const [day, month, year] = australianDate.split("/");
      const formattedDate = `${year}-${month}-${day}`;

      return {
        heading: isQuote ? "Renewal Quote" : "Invoice",
        company: {
          name: "Figmatic Pty Ltd",
          alias: '(DBA "Hypermatic")',
          website: "https://hypermatic.com",
          email: "adam@hypermatic.com",
          address: "2/21 Lambourn Road,\nWatsonia, VIC 3087, Australia",
          duns: "750156612"
        },
        customer: {
          name: "", // eg. Workday, Inc.
          bill_to: "" // eg. 6110 Stoneridge Mall Road\nPleasanton, CA 94588\nUnited States of America
        },
        invoice: {
          number: this.generateInvoiceNumber(),
          date: formattedDate
        },
        purchaseOrder: "",
        items: [
          {
            description: "", // eg. Bannerify Pro - 1 Year License Renewal (15 Seats)
            price: 0 // eg. 5616
          }
        ],
        paymentDetails: {
          bankName: "Westpac",
          bankAddress: "37 High St, Berwick VIC 3806, Australia",
          swiftCode: "WPACAU2S",
          accountName: "Figmatic Pty Ltd",
          bsb: "033-609",
          accountNumber: "584019"
        },
        includeCompanyDetails: true,
        includeCustomerDetails: true,
        includePaymentDetails: true,
        includeDunsDetails: false,
        isQuote: isQuote,
        metadata: ""
      };
    },
    pricePerSeat(seatCount, invoiceTotal) {
      const perSeat = Math.round(invoiceTotal / seatCount);
      return this.formatNumberToCurrency(perSeat);
    },
    formatNumberToCurrency(numberAmount) {
      // Convert to number and ensure two decimal places
      const formattedNumber = parseFloat(numberAmount).toFixed(2);
      // Add commas for thousands
      return `$${formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    },
    formatRenewalDate(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp.seconds * 1000);
      // Function to get the ordinal suffix for a number

      // Function to get the ordinal suffix for a number
      const getOrdinalSuffix = day => {
        if (day > 3 && day < 21) return "th"; // Handle special case for 11th, 12th, 13th, etc.
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      // Use Intl.DateTimeFormat for localization
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "Australia/Sydney"
      };

      const formatter = new Intl.DateTimeFormat("en-AU", options);

      // Get the formatted parts
      const parts = formatter.formatToParts(date);
      let day, month;

      for (const part of parts) {
        if (part.type === "day") {
          day = part.value;
        } else if (part.type === "month") {
          month = part.value;
        }
      }

      // Construct the final date string with the ordinal suffix
      return `${day}${getOrdinalSuffix(day)} ${month}`;
    },
    handleError(error) {
      this.user = {};
      switch (error.code) {
        case "auth/wrong-password":
          this.error = `Oops! That password isn't correct.`;
          break;
        case "auth/user-not-found":
          this.error = `Oops! This user wasn't found.`;
          break;
        default:
          this.error = error.message;
      }
    },

    generateInvoiceNumber() {
      return `#${new Date()
        .toISOString()
        .slice(8, 10)}${new Date()
        .toISOString()
        .slice(5, 7)}${new Date().toISOString().slice(2, 4)}`;
    },
    addItem() {
      this.formData.items.push({ description: "", price: 0 });
    },
    removeItem(index) {
      this.formData.items.splice(index, 1);
    },
    formatInvoiceDate(dateString) {
      const [y, m, d] = dateString.split("-").map(Number);
      const date = new Date(y, m - 1, d); // Month is 0-based in JavaScript

      // Function to get the ordinal suffix for a number
      const getOrdinalSuffix = day => {
        if (day > 3 && day < 21) return "th"; // Handle special case for 11th, 12th, 13th, etc.
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      // Use Intl.DateTimeFormat for localization
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "Australia/Melbourne"
      };

      const formatter = new Intl.DateTimeFormat("en-AU", options);

      // Get the formatted parts
      const parts = formatter.formatToParts(date);
      let day, month, year;

      for (const part of parts) {
        if (part.type === "day") {
          day = part.value;
        } else if (part.type === "month") {
          month = part.value;
        } else if (part.type === "year") {
          year = part.value;
        }
      }

      // Construct the final date string with the ordinal suffix
      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    },
    generatePdfInvoice() {
      const doc = new jsPDF({
        format: "a4", // A4 size
        unit: "mm" // Units in millimeters
      });

      const pageMargin = 16;

      let y = pageMargin;

      const {
        heading,
        company,
        customer,
        invoice,
        purchaseOrder,
        items,
        paymentDetails,
        isQuote
      } = this.formData;

      // Add the logo to the top-left corner
      const logo = this.loadLogo(); // Fetch logo as Base64
      if (logo) {
        doc.addImage(logo, "PNG", pageMargin, pageMargin - 3, 10, 10); // Position (x=10, y=10), Size (width=30, height=30)
        y += 15; // Adjust the y-coordinate to avoid overlapping
      }

      // Company Details
      if (this.formData.includeCompanyDetails) {
        doc.setFontSize(14).setFont("helvetica", "bold");
        doc.text(company.name, pageMargin, y);
        y += 6;

        if (company.alias) {
          doc.setFontSize(10).setFont("helvetica", "normal");
          doc.text(company.alias, pageMargin, y);
          y += 6;
        }

        if (this.formData.includeDunsDetails) {
          doc.text(`DUNS: ${company.duns}`, pageMargin, y);
          y += 6;
        }

        y = pageMargin;

        if (company.website) {
          doc.setTextColor(0, 0, 0);
          doc.textWithLink(
            company.website,
            doc.internal.pageSize.getWidth() - pageMargin,
            y,
            {
              align: "right"
            }
          );
          y += 6;
        }

        if (company.email) {
          doc.setTextColor(0, 0, 0);
          doc.text(
            company.email,
            doc.internal.pageSize.getWidth() - pageMargin,
            y,
            {
              align: "right"
            }
          );
          y += 8;
        }

        if (company.address) {
          const addressLines = company.address.split("\n");
          addressLines.forEach(line => {
            doc.text(line, doc.internal.pageSize.getWidth() - pageMargin, y, {
              align: "right"
            });
            y += 5;
          });
        }

        y += 6;
      }

      if (this.formData.includeDunsDetails) {
        y += 6;
      }

      // Set line width to a thin value
      doc.setLineWidth(0.2); // Equivalent to approximately 1px

      doc.setDrawColor(200, 200, 200); // Grey line
      doc.line(pageMargin, y, doc.internal.pageSize.getWidth() - pageMargin, y); // Draw the line

      y += 12;

      doc.setFont("helvetica", "normal").setFontSize(12);
      doc.text(
        `${this.formatInvoiceDate(invoice.date)}`,
        doc.internal.pageSize.getWidth() - pageMargin,
        y,
        {
          align: "right"
        }
      );

      if (isQuote) {
        doc.setFontSize(18).setFont("helvetica", "bold");
        doc.text(`${heading}`, pageMargin, y);
      } else if (invoice.number) {
        doc.setFontSize(18).setFont("helvetica", "bold");
        doc.text(`${heading}`, pageMargin, y);
        y += 7;
        doc.setFontSize(12).setFont("helvetica", "normal");
        doc.text(`${invoice.number}`, pageMargin, y);
      }

      y += 12;

      // Customer Details
      if (this.formData.includeCustomerDetails) {
        doc.setFontSize(12).setFont("helvetica", "bold");
        doc.text("Customer", pageMargin, y);

        y += 7;

        if (customer.bill_to === "" && customer.name) {
          doc.setFont("helvetica", "normal").setFontSize(12);
          doc.text(customer.name, pageMargin, y);
          y += 12;
        } else if (customer.bill_to) {
          // y += 12;

          // doc.setFontSize(12).setFont("helvetica", "bold");
          // doc.text("Bill To", pageMargin, y);

          doc.setFont("helvetica", "normal").setFontSize(12);

          const addressLines = customer.bill_to.split("\n");
          addressLines
            .filter(line => line !== "")
            .forEach(line => {
              doc.text(line, pageMargin, y);
              y += 6;
            });

          y += 6;
        }
      }

      // Invoice Details

      doc.setFontSize(12).setFont("helvetica", "bold");

      // Purchase Order
      if (purchaseOrder && !isQuote) {
        doc.text(`Purchase Order`, pageMargin, y);
        doc.setFontSize(12).setFont("helvetica", "normal");
        y += 7;
        doc.text(`${purchaseOrder}`, pageMargin, y);
        y += 12;
      }

      // Line Items

      doc.setFontSize(12).setFont("helvetica", "bold");

      doc.text("Items", pageMargin, y);
      y += 7;

      doc.setFontSize(12).setFont("helvetica", "normal");

      let itemsTotalPrice = 0;

      items.forEach((item, index) => {
        doc.text(`${index + 1}. ${item.description}`, pageMargin, y);
        const price = parseFloat(item.price) || 0; // Parse price as float, fallback to 0 if invalid
        doc.text(
          `${this.formatNumberToCurrency(price.toFixed(2))} USD`,
          doc.internal.pageSize.getWidth() - pageMargin,
          y,
          { align: "right" }
        );
        itemsTotalPrice += price;
        y += 6;
      });

      // Format final total with two decimal places
      itemsTotalPrice = parseFloat(itemsTotalPrice.toFixed(2));

      // y += 4;

      doc.line(pageMargin, y, doc.internal.pageSize.getWidth() - pageMargin, y); // Draw the line

      y += 8;

      doc.setFontSize(12).setFont("helvetica", "bold");
      doc.text(`Total`, doc.internal.pageSize.getWidth() - pageMargin, y, {
        align: "right"
      });
      y += 7;
      doc.setFontSize(12).setFont("helvetica", "normal");
      doc.text(
        `${this.formatNumberToCurrency(itemsTotalPrice)} USD`,
        doc.internal.pageSize.getWidth() - pageMargin,
        y,
        {
          align: "right"
        }
      );

      // Add Metadata if provided
      if (this.formData.metadata) {
        y += 6;

        doc.line(
          pageMargin,
          y,
          doc.internal.pageSize.getWidth() - pageMargin,
          y
        ); // Draw the line

        y += 10;
        doc.setFontSize(12).setFont("helvetica", "normal");
        const metadataLines = doc.splitTextToSize(
          this.formData.metadata,
          doc.internal.pageSize.getWidth() - 2 * pageMargin
        );
        metadataLines.forEach((line, index) => {
          doc.text(line, pageMargin, y);
          if (index !== metadataLines.length - 1) {
            y += 6;
          }
        });
      }

      // Payment Details

      if (this.formData.includePaymentDetails && !isQuote) {
        y += 6;

        doc.line(
          pageMargin,
          y,
          doc.internal.pageSize.getWidth() - pageMargin,
          y
        ); // Draw the line

        y += 10;

        doc.setFontSize(12).setFont("helvetica", "bold");
        doc.text("Payment Details", pageMargin, y);
        y += 7;

        doc.setFontSize(12).setFont("helvetica", "normal");

        doc.text(`Bank Name: ${paymentDetails.bankName}`, pageMargin, y);
        y += 6;

        doc.text(`Bank Address: ${paymentDetails.bankAddress}`, pageMargin, y);
        y += 6;

        doc.text(`SWIFT Code: ${paymentDetails.swiftCode}`, pageMargin, y);
        y += 6;
        doc.text(`Account Name: ${paymentDetails.accountName}`, pageMargin, y);
        y += 6;
        doc.text(`BSB: ${paymentDetails.bsb}`, pageMargin, y);
        y += 6;
        doc.text(
          `Account Number: ${paymentDetails.accountNumber}`,
          pageMargin,
          y
        );
        y += 10;
      }

      // Convert PDF to Blob and create preview URL
      const pdfBlob = doc.output("blob");
      this.pdfPreviewUrl = URL.createObjectURL(pdfBlob);
    },
    downloadPdf() {
      const link = document.createElement("a");
      // create a blobURI pointing to our Blob
      link.href = this.pdfPreviewUrl;
      link.download = `Hypermatic - ${this.formData.customer.name} ${
        this.formData.heading
      } (${new Date().toLocaleDateString("en-US", {
        timeZone: "Australia/Melbourne",
        year: "numeric",
        month: "long",
        day: "numeric"
      })})`;
      // some browser needs the anchor to be in the doc
      document.body.append(link);
      link.click();
      link.remove();
    },
    loadLogo() {
      return `data:image/png;base64,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`;
    }
  }
};
</script>

<style lang="scss">
html {
  // background: linear-gradient(45deg, #e978a0, #51b4db);
  // background-repeat: no-repeat;
  // background-position: fixed;
  min-height: 100%;
  background-color: #fcfcfc;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='28'%3E%3Cpath fill='%23ccc' fill-opacity='.1' d='M56 26v2h-7.75c2.3-1.27 4.94-2 7.75-2zm-26 2a2 2 0 10-4 0h-4.09A25.98 25.98 0 000 16v-2c.67 0 1.34.02 2 .07V14a2 2 0 00-2-2v-2a4 4 0 013.98 3.6 28.09 28.09 0 012.8-3.86A8 8 0 000 6V4a9.99 9.99 0 018.17 4.23c.94-.95 1.96-1.83 3.03-2.63A13.98 13.98 0 000 0h7.75c2 1.1 3.73 2.63 5.1 4.45 1.12-.72 2.3-1.37 3.53-1.93A20.1 20.1 0 0014.28 0h2.7c.45.56.88 1.14 1.29 1.74 1.3-.48 2.63-.87 4-1.15-.11-.2-.23-.4-.36-.59H26v.07a28.4 28.4 0 014 0V0h4.09l-.37.59c1.38.28 2.72.67 4.01 1.15.4-.6.84-1.18 1.3-1.74h2.69a20.1 20.1 0 00-2.1 2.52c1.23.56 2.41 1.2 3.54 1.93A16.08 16.08 0 0148.25 0H56c-4.58 0-8.65 2.2-11.2 5.6 1.07.8 2.09 1.68 3.03 2.63A9.99 9.99 0 0156 4v2a8 8 0 00-6.77 3.74c1.03 1.2 1.97 2.5 2.79 3.86A4 4 0 0156 10v2a2 2 0 00-2 2.07 28.4 28.4 0 012-.07v2c-9.2 0-17.3 4.78-21.91 12H30zM7.75 28H0v-2c2.81 0 5.46.73 7.75 2zM56 20v2c-5.6 0-10.65 2.3-14.28 6h-2.7c4.04-4.89 10.15-8 16.98-8zm-39.03 8h-2.69C10.65 24.3 5.6 22 0 22v-2c6.83 0 12.94 3.11 16.97 8zm15.01-.4a28.09 28.09 0 012.8-3.86 8 8 0 00-13.55 0c1.03 1.2 1.97 2.5 2.79 3.86a4 4 0 017.96 0zm14.29-11.86c1.3-.48 2.63-.87 4-1.15a25.99 25.99 0 00-44.55 0c1.38.28 2.72.67 4.01 1.15a21.98 21.98 0 0136.54 0zm-5.43 2.71c1.13-.72 2.3-1.37 3.54-1.93a19.98 19.98 0 00-32.76 0c1.23.56 2.41 1.2 3.54 1.93a15.98 15.98 0 0125.68 0zm-4.67 3.78c.94-.95 1.96-1.83 3.03-2.63a13.98 13.98 0 00-22.4 0c1.07.8 2.09 1.68 3.03 2.63a9.99 9.99 0 0116.34 0z'/%3E%3C/svg%3E");
  background-size: 80px;
}

body {
  background: none;
}

.home {
  padding-top: 82px;
}

@keyframes slideUp {
  0% {
    transform: translateY(40px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.home__content {
  animation: slideUp 600ms 300ms ease both;
  max-width: 500px;
  padding: 24px;
  margin: 44px auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);
}

.customers__billing {
  width: 24px;
  height: auto;
}

.customers__subscription {
  border-bottom: 1px solid #eee;
}

.customers__icon {
  width: 24px;
  height: 24px;
}

.home__content--secondary {
  animation-delay: 370ms;
  max-width: 1000px;
}

.customers__wrapper {
  margin: auto;
  max-width: 1500px;
  margin: 0 auto;
  padding: 0 24px;
}

.customers__content {
  animation: slideUp 600ms 300ms ease both;
  max-width: none;
  width: 100%;
  padding: 24px;
  margin: 44px auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);
}

.customers__summary,
.customers__table {
  width: 100%;
  border-spacing: 0;
  margin-top: 24px;

  thead {
    td {
      font-weight: bold;
    }
  }

  tbody tr:hover {
    background: #fff;
  }

  td {
    vertical-align: top;
  }
}

.customers__table tbody tr:hover {
  // background: #f1f1f1;
}

.customers__subscriptions {
  width: 100%;
}

.customers__subscriptions--inactive {
  text-decoration: line-through;
}

.customers__icontext {
  svg,
  img,
  span {
    vertical-align: middle;
    display: inline-block;
    margin-right: 6px;
  }
}

.customers__split {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.customers__button {
  appearance: none;
  // width: 100%;
  outline: 0;
  border: 0;
  color: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  background-color: #2f3d4c;
  box-shadow: 0 2px 4px rgba(#2f3d4c, 0.2);
  transition: all 300ms ease;
  display: block;
  text-align: center;
  padding: 8px 16px;
  margin-top: 8px;
  margin-bottom: 32px;
  margin-right: 8px;

  &:focus,
  &:hover {
    background-color: lighten(#2f3d4c, 5%);
    box-shadow: 0 4px 6px rgba(#2f3d4c, 0.25);
  }

  &:active {
    background-color: #2f3d4c;
    box-shadow: 0 2px 4px rgba(#2f3d4c, 0.2);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      background-color: #2f3d4c;
      opacity: 0.3;
      cursor: not-allowed;
      box-shadow: 0 2px 4px rgba(#2f3d4c, 0.2);
    }
  }
}

.firefoo__header .customers__button {
  margin-top: 0;
  margin-bottom: 0;
}

.customers__button--destroy {
  background-color: white;
  color: red;
  border: 1px solid red;

  &:focus,
  &:hover {
    background-color: red;
    color: white;
  }

  &:active {
    background-color: red;
    color: white;
  }
}

.invoice {
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
}

.invoice__form {
  margin: 0 auto;
  width: 50%;
  padding-right: 32px;
}

fieldset {
  margin-bottom: 20px;
  padding: 10px;
  border: 1px solid #ccc;
}

textarea,
input[type="number"],
input[type="text"],
input[type="date"] {
  font-family: "Arial", sans-serif;
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  min-height: 44px;
  margin-bottom: 8px;
  appearance: none;
  font-size: 16px;
  line-height: 32px;
  font-weight: 400;
  color: rgb(26, 31, 54);
  outline: 0;
  border: 1px solid #ccc;
  transition: all 300ms ease;
  background: rgb(255, 255, 255);

  &:active,
  &:focus {
    border: 1px solid #555;
    box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(60, 66, 87, 0.16) 0px 0px 0px 1px, rgba(0, 0, 0, 0) 0px 0px 0px 0px,
      rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.invoice__preview {
  width: 50%;
}

.invoice__iframe {
  width: 100%;
  border: 0;
  height: 1050px;
  border-radius: 8px;
}

.invoice__split {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
